import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const steps = [
  {
    n: "01",
    label: "Discover",
    title: "Listen, learn, define.",
    body:
      "Every project starts with a deep dive into your goals, audience and constraints. We map the problem before touching any code.",
    bullets: ["Goals workshop", "Stack audit", "Scope & timeline"],
    accent: "teal",
  },
  {
    n: "02",
    label: "Design",
    title: "Architect the experience.",
    body:
      "From wireframes to motion specs, we prototype the interaction model so you can feel the product before it ships.",
    bullets: ["Wireframes", "UI system", "Motion spec"],
    accent: "magenta",
  },
  {
    n: "03",
    label: "Build",
    title: "Engineer with care.",
    body:
      "Clean architecture, typed contracts, automated tests. We write code your future self will be glad to read.",
    bullets: ["Clean architecture", "Tests + CI", "Performance budget"],
    accent: "amber",
  },
  {
    n: "04",
    label: "Launch",
    title: "Ship, measure, iterate.",
    body:
      "Production-grade releases with monitoring, analytics, and a roadmap for the next 90 days. Done is just the start.",
    bullets: ["Monitoring", "Analytics", "90-day roadmap"],
    accent: "teal",
  },
];

const accentMap = {
  teal: "from-aurora-teal to-aurora-mint",
  magenta: "from-aurora-magenta to-aurora-rose",
  amber: "from-aurora-amber to-aurora-gold",
};

function Step({ step, idx }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 80%", "end 30%"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.4, 1, 1, 0.6]);

  return (
    <motion.div
      ref={ref}
      style={{ y, opacity }}
      className="relative grid grid-cols-[auto,1fr] gap-5 md:gap-8"
    >
      {/* Marker on the rail */}
      <div className="relative flex flex-col items-center">
        <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-full glass-strong border border-white/15 flex items-center justify-center">
          <span className="text-[11px] md:text-xs font-semibold text-white tracking-widest">
            {step.n}
          </span>
          <div className={`absolute -inset-1 rounded-full bg-gradient-to-br ${accentMap[step.accent]} opacity-20 blur-md`} />
        </div>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, x: 90 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.1 + idx * 0.05 }}
        className="relative glass rounded-2xl p-6 md:p-8 mb-6 md:mb-10"
      >
        <div className="text-[11px] uppercase tracking-[0.32em] text-white/55 mb-3">
          Step · {step.label}
        </div>
        <h3 className="font-jamjuree text-2xl md:text-3xl font-semibold text-white mb-4 leading-snug">
          {step.title}
        </h3>
        <p className="text-sm md:text-base text-white/65 leading-relaxed max-w-xl">
          {step.body}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {step.bullets.map((b) => (
            <span
              key={b}
              className="text-[11px] px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-white/75"
            >
              {b}
            </span>
          ))}
        </div>

        <div
          className={`absolute -inset-px rounded-2xl pointer-events-none opacity-20 bg-gradient-to-br ${accentMap[step.accent]} blur-xl`}
          style={{ zIndex: -1 }}
        />
      </motion.div>
    </motion.div>
  );
}

const ProcessSection = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 70%", "end 30%"],
  });
  const railHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      id="process"
      className="relative w-full px-6 md:px-12 lg:px-24 py-16 md:py-24 overflow-hidden"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vw] h-[60vw] bg-aurora-teal/5 blur-[140px] rounded-full pointer-events-none" />

      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <span className="eyebrow">
            <span className="dot" />
            How we work
          </span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 font-jamjuree text-3xl md:text-5xl lg:text-6xl font-semibold text-white leading-tight"
          >
            From idea to{" "}
            <span className="text-gradient-aurora">production</span>.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-5 text-sm md:text-base text-white/65 max-w-xl mx-auto leading-relaxed"
          >
            Four stages, no surprises. The same process powers every project
            we ship — whether it's a marketing site or a multi-platform app.
          </motion.p>
        </div>

        <div ref={sectionRef} className="relative">
          {/* Background rail */}
          <div className="absolute left-6 md:left-7 top-0 bottom-0 w-[2px] bg-white/[0.06] rounded-full" />
          {/* Animated progress rail */}
          <motion.div
            style={{ height: railHeight }}
            className="absolute left-6 md:left-7 top-0 w-[2px] rounded-full"
          >
            <div className="w-full h-full rounded-full bg-gradient-to-b from-aurora-teal via-aurora-magenta to-aurora-amber" />
          </motion.div>

          <div className="relative">
            {steps.map((step, i) => (
              <Step key={step.n} step={step} idx={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
